import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { FormSubmission } from '@/types/form.types'
import { ApiService } from '@/services/api.service'
import type { PersistedStateOptions } from 'pinia-plugin-persistedstate'

declare module 'pinia' {
  export interface DefineStoreOptionsBase<S, Store extends DefineStoreOptionsBase<S, Store>> {
    persist?: boolean | PersistedStateOptions | PersistedStateOptions[]
  }
}

export interface FormState {
  formData: FormSubmission
  isSubmitting: boolean
  error: string | null
  lastSubmissionId: string | null
}


export const useFormStore = defineStore(
  'form',
  () => {
    const formData = ref<FormSubmission>({} as FormSubmission)
    const isSubmitting = ref(false)
    const error = ref<string | null>(null)
    const lastSubmissionId = ref<string | null>(null)

    async function submitForm(data: FormSubmission): Promise<boolean> {
      isSubmitting.value = true
      error.value = null
      try {
        const response = await ApiService.submitForm(data)
        if (response.message === 'Form submitted successfully') {
          formData.value = { ...data }
          lastSubmissionId.value = response.submissionId || null
          return true
        }
        throw new Error(response.message)
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Submission failed'
        return false
      } finally {
        isSubmitting.value = false
      }
    }

    function reset() {
      formData.value = {} as FormSubmission
      isSubmitting.value = false
      error.value = null
      lastSubmissionId.value = null
    }

    return { formData, isSubmitting, error, lastSubmissionId, submitForm, reset }
  },
  {
    persist: {
      storage: localStorage,
      paths: ['formData', 'lastSubmissionId'],
    },
  },
)
